import {
  addMonths,
  endOfMonth,
  format,
  isAfter,
  isSameDay,
  isValid,
  parseISO,
  startOfMonth,
  subMonths
} from 'date-fns';
import { apiDateFormat, defaultDateFormat } from '../constants/dateFormats';
import { enUS } from 'date-fns/locale';

export function formatDate(
  date: string | number | Date,
  dateFormat: string = defaultDateFormat
): string {
  return format(new Date(date), dateFormat);
}

export const parseStringToDate = (dateString: string): Date | null => {
  return isValid(parseISO(dateString)) ? parseISO(dateString) : null;
};

export const hasDatePassed = (date: Date | null): boolean | null => {
  const today = new Date();
  return date !== null && isValid(date)
    ? isSameDay(today, date) || isAfter(today, date)
    : null;
};

export const getMonthNameForLocale = (
  locale: string,
  offsetFromCurrentMonth: number,
  date: Date | null = null
): string => {
  const startingMonth = !date ? new Date() : date;
  const targetMonth = addMonths(startingMonth, offsetFromCurrentMonth);
  const options: Intl.DateTimeFormatOptions = { month: 'long' };
  return targetMonth.toLocaleDateString(locale, options);
};

export const dateToApiFormat = (date: Date | number | null): string => {
  return date ? format(date, apiDateFormat) : '';
};

export const apiToDefaultDateFormat = (apiDate?: Date | string): string => {
  if (!apiDate) {
    return '';
  }
  const date = new Date(apiDate);
  return format(date, defaultDateFormat);
};

export const getFirstDayOfCurrentMonth = (): string => {
  const date = new Date();
  return format(startOfMonth(date), apiDateFormat);
};

export const getFirstDayOfLastMonth = (): string => {
  const date = new Date();
  const lastMonth = subMonths(date, 1);
  return format(startOfMonth(lastMonth), apiDateFormat);
};

export const getLastDayOfCurrentMonth = (): string => {
  const date = new Date();
  return format(endOfMonth(date), apiDateFormat);
};

export const getLastDayOfLastMonth = (): string => {
  const date = new Date();
  const lastMonth = subMonths(date, 1);
  return format(endOfMonth(lastMonth), apiDateFormat);
};

export const isFuture = (date: string) => {
  return isAfter(new Date(date), new Date());
};

export const monthDiff = (dateFrom: Date, dateTo: Date) => {
  return (
    dateTo.getMonth() -
    dateFrom.getMonth() +
    12 * (dateTo.getFullYear() - dateFrom.getFullYear())
  );
};

export const getStartOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const getEndOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

export const getFirstDayOfNextMonth = (): string => {
  const date = startOfMonth(addMonths(new Date(), 1));

  return format(date, 'dd.MM.yyyy', { locale: enUS });
};

const getLocalDateComponents = (
  date: Date | string | number
): { year: number; month: number; day: number } => {
  if (date instanceof Date) {
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate()
    };
  }

  if (typeof date === 'string') {
    const parts = date.split(/[-/]/);
    if (parts.length === 3) {
      return {
        year: Number.parseInt(parts[0], 10),
        month: Number.parseInt(parts[1], 10) - 1,
        day: Number.parseInt(parts[2], 10)
      };
    }
  }

  const d = new Date(date);
  return {
    year: d.getFullYear(),
    month: d.getMonth(),
    day: d.getDate()
  };
};

export const formatDateToUTCISOStart = (
  date: Date | string | number | null | undefined
): string => {
  if (!date) return '';

  const { year, month, day } = getLocalDateComponents(date);
  const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  return utcDate.toISOString();
};

export const formatDateToUTCISOEnd = (
  date: Date | string | number | null | undefined
): string => {
  if (!date) return '';

  const { year, month, day } = getLocalDateComponents(date);
  const utcDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
  return utcDate.toISOString();
};

const getTimezoneOffsetString = (date: Date): string => {
  const pad = (n: number, length = 2) => String(n).padStart(length, '0');
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);
  return `${sign}${offsetHours}:${offsetMinutes}`;
};

export const toLocalISOString = (date: Date = new Date()): string => {
  const dateTime = format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS");
  return `${dateTime}${getTimezoneOffsetString(date)}`;
};
